/**
 * #1124 part B1 — page script memory for the Data pressure card.
 *
 * Stock Chrome rounds `performance.memory` into about 100 exponential buckets
 * (steps of about 6 %) and refreshes it at most every 20 minutes; only a
 * browser started with `--enable-precise-memory-info` reads it live. So the
 * card shows heap now and growth since the page opened, which over a trading
 * session is the leak-shaped signal, and never a per-minute slope.
 * `jsHeapSizeLimit` is rounded the same way. Firefox and Safari have no API:
 * every reading is null there, rendered "—".
 *
 * Fixed size: the page-open reading (two numbers, kept for the page's life on
 * purpose) and a 32-slot ring of distinct later readings, reset when the last
 * card unmounts.
 */

interface RawMemory { used: number; limit: number | null }

function readRaw(): RawMemory | null {
  const m = (performance as Performance & { memory?: { usedJSHeapSize?: number; jsHeapSizeLimit?: number } }).memory;
  const used = m?.usedJSHeapSize;
  if (typeof used !== 'number' || !Number.isFinite(used) || used <= 0) return null;
  const limit = m?.jsHeapSizeLimit;
  return { used, limit: typeof limit === 'number' && Number.isFinite(limit) && limit > 0 ? limit : null };
}

/** Under one of Chrome's bucket steps the readings can't tell growth from noise. */
const BUCKET_STEP = 0.06;
const SLOPE_MIN_READINGS = 3;
const SLOPE_MIN_SPAN_MS = 40 * 60_000;
const RING = 32;

// Taken at module load. This module is imported (through the card) by Game
// Detail, which App imports statically, so it loads at app boot: growth is
// measured from the page opening, not from a card mounting. Load-bearing.
const firstRaw = readRaw();
const firstT = performance.now();

const ringV = new Float64Array(RING);
const ringT = new Float64Array(RING);
let ringNext = 0;
let ringFilled = 0;

function lastIndex(): number {
  return (ringNext - 1 + RING) % RING;
}

function lastValue(): number | null {
  if (ringFilled > 0) return ringV[lastIndex()];
  return firstRaw?.used ?? null;
}

/** Record the current reading if it differs from the last one. Called by the
 *  card's sampler every 10 s; Chrome's value can't change faster than that. */
export function samplePageMemory(): void {
  const raw = readRaw();
  if (raw == null || raw.used === lastValue()) return;
  ringV[ringNext] = raw.used;
  ringT[ringNext] = performance.now();
  ringNext = (ringNext + 1) % RING;
  if (ringFilled < RING) ringFilled += 1;
}

/** Distinct readings held, the page-open one included. */
export function pageMemoryReadings(): number {
  return (firstRaw ? 1 : 0) + ringFilled;
}

export interface PageMemoryReading {
  usedBytes: number;
  /** Rounded by Chrome like the heap; shown as "about". */
  limitBytes: number | null;
  /** Heap now minus the page-open reading; null when there was none. */
  growthBytes: number | null;
  /** The change is under one bucket step: "about the same". */
  sameAsStart: boolean;
  pageAgeMs: number;
  /** Copy report only: page open to the latest distinct reading, per hour. */
  slopeBytesPerHour: number | null;
}

export function readPageMemory(): PageMemoryReading | null {
  const raw = readRaw();
  if (raw == null) return null;
  const growth = firstRaw ? raw.used - firstRaw.used : null;
  let slope: number | null = null;
  if (firstRaw && pageMemoryReadings() >= SLOPE_MIN_READINGS) {
    const i = lastIndex();
    const span = ringT[i] - firstT;
    if (span >= SLOPE_MIN_SPAN_MS) slope = ((ringV[i] - firstRaw.used) / span) * 3_600_000;
  }
  return {
    usedBytes: raw.used,
    limitBytes: raw.limit,
    growthBytes: growth,
    sameAsStart: growth != null && Math.abs(growth) < BUCKET_STEP * Math.max(raw.used, firstRaw!.used),
    pageAgeMs: performance.now(),
    slopeBytesPerHour: slope,
  };
}

/** The last card unmounted. The page-open reading stays. */
export function resetPageMemoryRing(): void {
  ringV.fill(0);
  ringT.fill(0);
  ringNext = 0;
  ringFilled = 0;
}
